import React from "react";
import Button from "../../../ui-components/Button";
import {EnvelopeIcon} from "@heroicons/react/24/outline";
import Badge from "../../../ui-components/Badge";

const buildUserStatusLookup = (intl) =>
  ['active', 'churn', 'disabled', 'leaving', 'never_registered', 'new', 'passive', 'pending']
    .reduce((acc, i) => ({...acc, [i]: intl.formatMessage({id: i})}), {})

const buildUserRegistrationStatusLookup = (intl) =>
  ['active', 'inactive', 'pending_registration']
    .reduce((acc, i) => ({...acc, [i]: intl.formatMessage({id: `registration_${i}`})}), {})


const extractPendingInvitationEmail = (pendingInvitationsList) =>
  pendingInvitationsList?.[0]?.user_email?.trim();


export const CompanyInfoTypeComponent = ({company_info_type, intl}) => {
  switch (company_info_type) {
    case "CLI":
      return (
        <span
          class="bg-green-100 text-green-800 text-xs font-medium me-2 px-2 py-0.5 rounded dark:bg-green-900 dark:text-green-300">
              {intl.formatMessage({id: "client", defaultMessage: "cliente"})}
            </span>
      );
    case "DES":
      return (
        <span
          class="bg-blue-100 text-blue-800 text-xs font-medium me-2 px-2 py-0.5 rounded dark:bg-blue-900 dark:text-blue-300">
            {intl.formatMessage({id: "destination", defaultMessage: "destinazione"})}
          </span>
      );
  }
}

const getResalesType = (company_info, intl) => {
  if (company_info?.type) {
    return (
      <div className="flex items-center gap-2">
        <CompanyInfoTypeComponent company_info_type={company_info.type} intl={intl}/>
        <span>{company_info.name}</span>
      </div>
    );
  }
  return null;
}

export const getColumns = (intl, roles, sales, fields, setInvitation) => {
  let cols = [
    {
      title: intl.formatMessage({id: "name"}),
      field: "full_name",
      type: "string",
      headerStyle: {minWidth: 200},
      cellStyle: {minWidth: 200, textTransform: "uppercase"},
      render: ({id, full_name, registration_status, role, company_info, account_user}) => {
        const renderInvitationButton =
          ["resale", "resale_tintometer"].includes(role?.name.toLowerCase()) && // only resales can be invited
          ["inactive", "pending_registration"].includes(registration_status) &&
          setInvitation;
        return (
          <div>
            <div className="flex items-center gap-4">
              {/* --- user full name --- */}
              <span className="whitespace-nowrap">{full_name}</span>
              {account_user &&
                <span className="whitespace-nowrap">({account_user.full_name})</span>
              }
              {/* --- user invitation button --- */}
              {renderInvitationButton && (
                <Button
                  styleType="green"
                  onClick={() => {
                    setInvitation({
                      id,
                      role_name: intl.formatMessage({ id: role.name }),
                    });
                  }}
                  paddingCls="px-3 py-1"
                >
                  <EnvelopeIcon className="w-4 mr-1" />
                  <span className="text-xs">
                    {intl.formatMessage({ id: "invite" })}
                  </span>
                </Button>
              )}
            </div>
            {company_info || account_user?.company_info? getResalesType(company_info || account_user?.company_info, intl): null}
          </div>
        );
      },
    },
    {
      title: intl.formatMessage({id: "email"}),
      field: 'email',
      type: 'string',
      customFilterAndSearch: (term, {email, pending_invitations}) => {
        const trimmedMail =
          email?.trim() || extractPendingInvitationEmail(pending_invitations);
        return (
          !!trimmedMail &&
          trimmedMail.toUpperCase().indexOf(term.toUpperCase()) !== -1
        );
      },
      render: ({email, pending_invitations}) => {
        const trimmedUserEmail = email?.trim(),
          trimmedInvitationEmail =
            extractPendingInvitationEmail(pending_invitations);
        const emailToDisplay = trimmedUserEmail || trimmedInvitationEmail;
        if (emailToDisplay)
          return (
            <a
              className={`italic ${
                !trimmedUserEmail && !!trimmedInvitationEmail
                  ? "text-gray-400"
                  : "text-am-500"
              }`}
              href={"mailto:" + emailToDisplay}
              target="_blank"
              rel="noopener noreferrer"
            >
              {emailToDisplay}
            </a>
          );
        return (
          <span className="italic text-gray-400 whitespace-nowrap">
          {intl.formatMessage({id: "undefined"})}
        </span>
        );
      },
    },
    {
      title: intl.formatMessage({id: "registration_status"}),
      field: "registration_status",
      lookup: buildUserRegistrationStatusLookup(intl),
      cellStyle: {whiteSpace: "nowrap", textTransform: "uppercase"},
      hidden: true,
    },
    {
      title: intl.formatMessage({id: "user_status"}),
      field: "status",
      lookup: buildUserStatusLookup(intl),
      cellStyle: {whiteSpace: "nowrap", textTransform: "uppercase"},
    },
    ...(roles ? [{
      title: intl.formatMessage({id: "role"}),
      field: "role_id",
      lookup: roles,
      cellStyle: {whiteSpace: "nowrap", textTransform: "uppercase"},
      render: ({role_id, account_user}) => {
        return roles[account_user?.role_id || role_id];
      }
    }]: []),
    {
      title: intl.formatMessage({id: "city"}),
      field: "city",
      type: "string",
    },
    {
      title: intl.formatMessage({id: "address"}),
      field: "company_address",
      type: "string",
      cellStyle: {whiteSpace: "nowrap"},
    },
    {
      title: intl.formatMessage({id: "created_at"}),
      field: "created_at",
      defaultSort: "desc",
      render: ({created_at}) => (
        <span>
        {intl.formatDate(created_at, {
          day: "numeric",
          month: "short",
          year: "numeric",
        })}
      </span>
      ),
    },
  ];

  ['agente_ed', 'agente_pos', 'field_ed', 'field_pos', 'field_sup'].forEach(i => {
    cols = cols.concat([
      {
        title: intl.formatMessage({id: i}),
        field: i,
        lookup: i.startsWith('agente') ? sales : fields,
        headerStyle: {fontWeight: "bold", fontSize: 15, whiteSpace: "nowrap"},
        render: function (row) {
          return row[i] ? (
            <div>
              {row[i].split(",").map((s, idx) => {
                const value = i.startsWith('agente') ? sales[s] : fields[s];
                return !!value ? <Badge classNames="mb-1 mr-1" text={value} key={idx}/> : null;
              })}
            </div>
          ) : null;
        },
      },
    ])
  });

  return cols;
}

export const OPTIONS = {
  filtering: true,
  toolbar: false,
  sorting: true,
  emptyRowsWhenPaging: false,
  pageSize: 10,
  pageSizeOptions: [5, 10, 20, 50],
  headerStyle: {fontWeight: "bold", fontSize: 15},
  filterCellStyle: {paddingTop: 8, maxWidth: 200},
  cellStyle: {textTransform: "uppercase"},
};

export const LOCALIZATION = {
  header: {
    actions: "",
  },
  pagination: {
    labelDisplayedRows: "{from}-{to} di {count} righe",
    labelRowsSelect: "Righe",
  },
};
